import { useEffect, useState, useRef } from 'react'
import { X, Loader2, ExternalLink, XCircle, CheckCircle } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'

// Mux Player web component is registered globally via <script> in index.html.
// Using start-time / end-time attributes enables instant clipping (free,
// segment-level accuracy).
//
// Reference: https://docs.mux.com/guides/mux-player-web

function fmtTime(sec) {
  const totalSec = Math.floor(sec)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

// Step 26.5 / 27 Option B: ClipPreview accepts optional `onReject` and
// `onApprove` callbacks. When provided, both ❌ Reject and ✓ Approve
// buttons appear in the header. Callers own the dialog/POST via
// useReject + useApprove; this component is just the trigger surface.
export default function ClipPreview({ tbtv_id, startMs, endMs = null, preMs = 10000, postMs = 10000, title, onClose, onReject = null, onApprove = null }) {
  const api = useHighlightsApi()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const playerRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    setData(null)
    setErr(null)
    // Step 22: when the caller knows the moment's end_ms (e.g. KM Explorer),
    // forward it so the clip window respects the actual span instead of a
    // fixed ±10s around start_ms. Backwards compat — older callers that omit
    // endMs get identical behavior to pre-Step-22.
    api.clipUrl(tbtv_id, { t: startMs, endT: endMs, pre: preMs, post: postMs })
      .then(r => { if (!cancelled) setData(r) })
      .catch(e => { if (!cancelled) setErr(e.message) })
    return () => { cancelled = true }
  }, [tbtv_id, startMs, endMs, preMs, postMs])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Belt-and-suspenders auto-pause at clip end. Mux Player's `end-time`
  // attribute *should* stop playback at the boundary, but in practice it
  // sometimes continues 1-3s past as the player buffers — editorial saw
  // clips playing well into the next play. This timeupdate listener
  // hard-pauses the instant currentTime crosses data.end_seconds.
  useEffect(() => {
    const el = playerRef.current
    if (!el || !data?.end_seconds) return
    const onTimeUpdate = () => {
      if (el.currentTime >= data.end_seconds && !el.paused) {
        try { el.pause() } catch { /* ignore */ }
        // Clamp to exactly end so seeking back works cleanly.
        try { el.currentTime = data.end_seconds } catch { /* ignore */ }
      }
    }
    el.addEventListener('timeupdate', onTimeUpdate)
    return () => el.removeEventListener('timeupdate', onTimeUpdate)
  }, [data?.end_seconds, data?.playback_id])

  const headerTitle = title || data?.title || tbtv_id

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
      }}
    >
      <div style={{
        background: '#111', border: '1px solid #2a2a2a', borderRadius: 8,
        width: 'min(960px, 96vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {headerTitle}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 2, fontFamily: 'monospace' }}>
              {data
                ? `${fmtTime(data.start_seconds)} → ${fmtTime(data.end_seconds)}`
                : 'loading clip URL…'}
            </div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontFamily: 'monospace', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <span title="Click to copy" style={{ cursor: 'pointer' }}
                onClick={() => navigator.clipboard?.writeText(tbtv_id)}>
                tbtv_id: {tbtv_id}
              </span>
              {data?.playback_id && (
                <span title="Mux playback_id" style={{ cursor: 'pointer' }}
                  onClick={() => navigator.clipboard?.writeText(data.playback_id)}>
                  playback_id: {data.playback_id}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {onApprove && (
              <button
                onClick={() => { onApprove(); onClose?.() }}
                title="Approve this moment — feeds eval gold set + future RAG exemplars"
                style={{
                  padding: '4px 10px', fontSize: 12,
                  background: 'transparent', border: '1px solid #14532d',
                  borderRadius: 4, color: '#4ade80', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                <CheckCircle size={12} /> Approve
              </button>
            )}
            {onReject && (
              <button
                onClick={() => { onReject(); onClose?.() }}
                title="Reject this moment — hide now AND skip on future TBTV-AI re-runs of this video"
                style={{
                  padding: '4px 10px', fontSize: 12,
                  background: 'transparent', border: '1px solid #7f1d1d',
                  borderRadius: 4, color: '#fca5a5', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                <XCircle size={12} /> Reject
              </button>
            )}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div style={{ background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 360 }}>
          {!data && !err && (
            <div style={{ color: '#888', display: 'flex', alignItems: 'center', gap: 8, padding: 32 }}>
              <Loader2 size={14} className="spin" /> Loading…
            </div>
          )}
          {err && (
            <div style={{ color: 'crimson', padding: 32, textAlign: 'center' }}>
              {err}
            </div>
          )}
          {data && data.playback_id && (
            // Step 22: now also passes end-time so playback auto-stops at
            // the clip boundary instead of running on past the moment. The
            // window comes from the backend route which already computes
            // [start_t - pre, end_t + post] honoring the moment's span.
            <mux-player
              ref={playerRef}
              key={`${data.playback_id}-${data.start_seconds}-${data.end_seconds}`}
              playback-id={data.playback_id}
              start-time={data.start_seconds}
              end-time={data.end_seconds}
              metadata-video-title={headerTitle}
              stream-type="on-demand"
              accent-color="#3b82f6"
              autoplay="any"
              style={{ width: '100%', maxHeight: '70vh', aspectRatio: '16/9', display: 'block' }}
            />
          )}
          {data && !data.playback_id && data.playback_url && (
            // Fallback: no playback_id known (unusual). Use the raw URL.
            <video controls autoPlay src={data.playback_url}
              style={{ width: '100%', maxHeight: '70vh' }} />
          )}
        </div>

        {data && (
          <div style={{ padding: '8px 14px', borderTop: '1px solid #1f1f1f', fontSize: 11, color: '#666', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span>
              Preview anchored at {fmtTime(data.start_seconds)} (±10s marker window) · backend: {data.backend}
            </span>
            <a href={data.playback_url} target="_blank" rel="noopener noreferrer"
              style={{ color: '#888', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <ExternalLink size={11} /> Open URL
            </a>
          </div>
        )}

        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
      </div>
    </div>
  )
}
